import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import componentStyles from './se-alpha-listing.css.lit';
import { classMap } from 'lit/directives/class-map.js';

export const alphaListingBackgrounds = ['light', 'dark'] as const;

export type SeAlphaListingBackground = typeof alphaListingBackgrounds[number];

export const alphaListingVariants = ['full', 'search'] as const;

export type SeAlphaListingVariant = typeof alphaListingVariants[number];

export type SeAlphaListingLink = { text: string; url: string };

export type SeAlphaListingItem = {
  letter: string;
  url?: string;
  active: boolean;
  links: SeAlphaListingLink[];
};

/**
 * The SeAlphaListing component
 * @element se-alpha-listing
 */
@customElement('se-alpha-listing')
export class SeAlphaListing extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Sets text styles on mini list for use with different backgrounds.
   */
  @property({ type: String })
  background: SeAlphaListingBackground = 'light';

  /**
   * Stringified JSON object passed in by backend
   */
  @property({ type: String })
  listings: string;

  /**
   * "full" page version or "search" for use with search panel
   */
  @property({ type: String })
  variant: SeAlphaListingVariant = 'full';

  /**
   * Parsed result of this.listings.
   */
  @property({ type: Array })
  data: SeAlphaListingItem[] = [];

  alphabet = [
    'a',
    'b',
    'c',
    'd',
    'e',
    'f',
    'g',
    'h',
    'i',
    'j',
    'k',
    'l',
    'm',
    'n',
    'o',
    'p',
    'q',
    'r',
    's',
    't',
    'u',
    'v',
    'w',
    'x',
    'y',
    'z',
  ];

  render(): TemplateResult {
    return html`
      ${this.miniTemplate(this.data)} ${this.fullTemplate(this.data)}
    `;
  }

  firstUpdated() {
    this.data = JSON.parse(this.listings);
  }

  /**
   * Returns the mini/alphabet list
   */
  miniTemplate(data: SeAlphaListingItem[]) {
    const miniListClasses = {
      'se-alpha-listing--list-mini': true,
      'search': this.variant === 'search',
    };
    return html`
      <nav class="se-alpha-listing--nav-mini" aria-label="mini nav">
        <ul class="${classMap(miniListClasses)}">
          ${this.generateMiniListing(data)}
        </ul>
      </nav>
    `;
  }

  /**
   *
   * @param data
   * @returns the full page cards list.
   */
  fullTemplate(data: SeAlphaListingItem[]) {
    if (this.variant === 'search') {
      return;
    }
    return html`
      <nav class="se-alpha-listing--nav" aria-label="full nav">
        <ul class="se-alpha-listing--list">
          ${data.map((item, i) => this.fullItemCard(item, i))}
        </ul>
      </nav>
    `;
  }

  /**
   *
   * @param data
   * @returns link or placeholder template depending on data.
   */
  generateMiniListing(data: SeAlphaListingItem[]) {
    const dataEntries: Record<string, number> = {};
    data.map((entry, i) => (dataEntries[entry.letter] = i + 1));

    return this.alphabet.map(letter => {
      if (dataEntries[letter]) {
        return this.miniItemLinkTemplate(data[dataEntries[letter] - 1]);
      } else {
        return this.miniItemPlaceholderTemplate(letter);
      }
    });
  }

  miniItemLinkTemplate(item: SeAlphaListingItem) {
    const miniLinkClasses = {
      'se-alpha-listing--mini-link': true,
      'active': item.active,
      'search': this.variant === 'search',
    };
    const link =
      item.url && item.url !== '' ? item.url : `#jump-to-${item.letter}`;
    return html`<li
      class="se-alpha-listing--mini-item"
      @click="${this.scrollHandler}"
    >
      <a class="${classMap(miniLinkClasses)}" href="${link}">
        ${item.letter.toUpperCase()}
      </a>
    </li>`;
  }

  miniItemPlaceholderTemplate(letter: string) {
    const miniSpanClasses = {
      'se-alpha-listing--mini-span': true,
      'search': this.variant === 'search',
    };
    return html`<li class="se-alpha-listing--mini-item">
      <span class="${classMap(miniSpanClasses)}">${letter.toUpperCase()}</span>
    </li>`;
  }

  fullItemCard(item: SeAlphaListingItem, index: number) {
    const cardClasses = {
      'se-alpha-listing--card': true,
      'grayed': index % 2 !== 0,
      'white': index % 2 === 0,
    };

    return html`<li
      class="${classMap(cardClasses)}"
      id="jump-to-${item.letter}"
      tabindex="-1"
    >
      <span class="se-alpha-listing--letter">${item.letter.toUpperCase()}</span>
      <ul class="se-alpha-listing--card-list">
        ${item.links.map(link => this.fullItemLinkTemplate(link))}
      </ul>
    </li>`;
  }

  fullItemLinkTemplate(item: SeAlphaListingLink) {
    return html`<li class="se-alpha-listing--item">
      <a class="se-alpha-listing--link" href="${ifDefined(item.url)}">
        ${item.text}</a
      >
    </li>`;
  }

  /**
   *
   * @param e
   * Handles scrolling to specified letter on click.
   */
  scrollHandler(e: Event) {
    if (this.variant === 'search') {
      // bail
      return;
    } else {
      e.preventDefault();
      const target = (e.target as HTMLAnchorElement).getAttribute('href');
      const scrollTarget = this.shadowRoot?.querySelector(
        `${target}`
      ) as HTMLElement;
      scrollTarget?.scrollIntoView({ behavior: 'smooth' });

      setTimeout(() => {
        scrollTarget.focus();
      }, 400);
    }
  }
}
